<script setup lang="ts">
import { getFiles, uploadFiles } from '~/layers/cms/app/composables/cms'
import ConfirmDialog from 'primevue/confirmdialog'
import ContextMenu from 'primevue/contextmenu'
import DataView from 'primevue/dataview'
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import { useConfirm } from 'primevue/useconfirm'
import { useToast as usePrimeToast } from 'primevue/usetoast'
import { computed, inject, onMounted, ref } from 'vue'
import placeholder from '~/layers/cms/app/assets/images/placeholder.svg'

const confirm = useConfirm()
const imagesList = ref<string[]>([])
const loading = ref(false)
const uploading = ref(false)

const toast = usePrimeToast()
const menu = ref()
const chosen_elem = ref<string>()
const filterText = ref('')
const isDragOver = ref(false)
let dragCounter = 0

interface DialogRef {
  value: {
    data: {
      choose: boolean
    }
    close(payload: { src: string }): void
  }
}
const dialogRef = inject<DialogRef>('dialogRef')

const closeDialog = (src: string) => {
  if (!dialogRef || dialogRef.value.data.choose == false) return
  dialogRef.value.close({ src })
}

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault()
  dragCounter++
  isDragOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  dragCounter--
  if (dragCounter === 0) isDragOver.value = false
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
}

const handleDrop = async (event: DragEvent) => {
  event.preventDefault()
  dragCounter = 0
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (!files?.length) return
  await doUploadRaw(Array.from(files))
}

const refreshFilesList = async () => {
  const res = await getFiles()
  if (!res.status_ok) {
    toast.add({ severity: 'error', summary: 'Error', detail: res.message, life: 3000 })
    return
  }
  imagesList.value = res.data as string[]
}

onMounted(async () => {
  loading.value = true
  await refreshFilesList()
  loading.value = false
})

const filteredImages = computed<string[]>(() => {
  if (!filterText.value) return imagesList.value
  const filterLower = filterText.value.toLowerCase()
  return imagesList.value.filter(item => item.toLowerCase().includes(filterLower))
})

const onImageRightClick = (event: MouseEvent, item: string) => {
  if (!event.target) return
  chosen_elem.value = item
  menu.value.show(event)
}

const image_menu_items = [
  {
    label: 'Kopiuj link',
    command: () => {
      if (chosen_elem.value) {
        try {
          navigator.clipboard.writeText(chosen_elem.value)
        } catch {
          throw new Error('działa tylko na https')
        }
        toast.add({ severity: 'info', summary: 'Info', detail: 'Skopiowano link do pliku', life: 3000 })
      } else {
        toast.add({ severity: 'info', summary: 'Info', detail: 'Błąd, nie skopiowano linku', life: 3000 })
      }
    }
  }
]

const upload = async (event: FileUploadSelectEvent) => {
  const file = event.files[0]
  if (!file) return
  if (file.size > 10 * 1024 * 1024) {
    confirm.require({
      target: document.getElementsByClassName('uploadButton')[0] as HTMLElement,
      message: 'Plik ma ponad 10 MB, czy na pewno chcesz go dodać?',
      icon: 'pi pi-exclamation-triangle',
      rejectProps: { label: 'Anuluj', severity: 'secondary', outlined: true },
      acceptProps: { label: 'Dodaj' },
      accept: async () => await doUpload(event),
      reject: () => {}
    })
    return
  }
  await doUpload(event)
}

const doUpload = async (event: FileUploadSelectEvent) => {
  uploading.value = true
  const res = await uploadFiles(event.files)
  uploading.value = false
  if (!res.status_ok) {
    toast.add({ severity: 'error', summary: 'Error', detail: res.message, life: 3000 })
    return
  }
  toast.add({ severity: 'info', summary: 'Sukces', detail: 'Dodano plik', life: 3000 })
  await refreshFilesList()
}

const doUploadRaw = async (files: File[]) => {
  uploading.value = true
  const res = await uploadFiles(files)
  uploading.value = false
  if (!res.status_ok) {
    toast.add({ severity: 'error', summary: 'Error', detail: res.message, life: 3000 })
    return
  }
  toast.add({ severity: 'info', summary: 'Sukces', detail: 'Dodano plik', life: 3000 })
  await refreshFilesList()
}

const listType = ref<'grid' | 'list'>('grid')

const isFileMedia = (file: string): boolean => {
  const ext = file.split('.').pop()?.toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif'].includes(ext ?? '')
}
</script>

<template>
  <div
    class="h-full w-full overflow-y-auto relative"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div class="flex flex-col gap-4 p-4">
      <ConfirmDialog />

      <div class="flex items-center gap-3">
        <div class="flex gap-1">
          <button
            class="w-8 h-8 rounded hover:bg-black/5 cursor-pointer"
            :class="listType == 'list' ? 'bg-black/5' : ''"
            @click="listType = 'list'"
          >
            <i class="pi pi-list text-base" />
          </button>
          <button
            class="w-8 h-8 rounded hover:bg-black/5 cursor-pointer"
            :class="listType == 'grid' ? 'bg-black/5' : ''"
            @click="listType = 'grid'"
          >
            <i class="pi pi-th-large text-base" />
          </button>
        </div>

        <InputText
          v-model="filterText"
          type="text"
          placeholder="Filtruj listę plików..."
          class="ml-auto w-full"
        />
        <div class="flex items-center gap-3">
          <FileUpload
            class="uploadButton text-nowrap"
            mode="basic"
            name="newFiles[]"
            :auto="true"
            :multiple="true"
            :disabled="uploading"
            choose-label="Dodaj plik"
            @select="upload"
          />
          <ProgressSpinner
            v-if="uploading"
            style="width:24px;height:24px"
          />
        </div>
      </div>
    </div>

    <div
      v-if="isDragOver"
      class="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/70 backdrop-blur-sm text-sm font-medium text-gray-700 pointer-events-none"
    >
      Upuść pliki tutaj
    </div>

    <div
      v-if="loading"
      class="flex justify-center items-center py-20"
    >
      <ProgressSpinner />
    </div>

    <template v-else>
      <div
        v-if="filteredImages.length === 0"
        class="flex justify-center items-center py-20 text-gray-500 text-sm"
      >
        {{ filterText ? 'Brak wyników dla podanego filtra' : 'Nie znaleziono plików.' }}
      </div>

      <DataView
        v-else
        :value="filteredImages"
        paginator
        paginator-position="top"
        :rows="18"
        :layout="listType"
        class="w-full"
        :pt="{
          content: { class: [listType === 'grid' ? 'grid grid-cols-6 gap-0' : ''] }
        }"
      >
        <template #grid="slotProps">
          <div
            v-for="item in slotProps.items"
            :key="JSON.stringify(item)"
            class="flex cursor-pointer flex-col overflow-clip"
            @click="closeDialog(item)"
            @contextmenu="onImageRightClick($event, item)"
          >
            <div class="flex items-center justify-center h-30 p-2">
              <NuxtImg
                :src="isFileMedia(item) ? item : placeholder"
                class="max-w-full max-h-full object-contain"
                loading="lazy"
                alt="Plik nie jest obrazem"
                width="144"
                height="144"
                format="webp"
                quality="100"
              />
            </div>
            <div class="px-2 py-1 text-xs text-center text-nowrap truncate">
              {{ item.split('/').pop() }}
            </div>
          </div>
        </template>
        <template #list="slotProps">
          <div
            v-for="item in slotProps.items"
            :key="JSON.stringify(item)"
            class="flex items-center gap-4 px-4 py-2 cursor-pointer"
            @click="closeDialog(item)"
            @contextmenu="onImageRightClick($event, item)"
          >
            <div class="w-12 h-12 shrink-0 flex items-center justify-center">
              <NuxtImg
                :src="isFileMedia(item) ? item : placeholder"
                class="max-w-full max-h-full object-contain"
                loading="lazy"
                alt="Plik nie jest obrazem"
                width="48"
                height="48"
                format="webp"
                quality="100"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium truncate">
                {{ item.split('/').pop() }}
              </div>
              <div class="text-xs text-gray-500 truncate">
                {{ item }}
              </div>
            </div>
          </div>
        </template>
      </DataView>
    </template>

    <ContextMenu
      ref="menu"
      :model="image_menu_items"
    />
  </div>
</template>

<style lang="css">
.p-dataview-paginator-top {
  border-color: var(--color-gray-800);
}
</style>
